/**
 * Replaces [price] shortcodes in the context text with a styled price display.
 *
 * This function searches for [price] shortcodes in the provided context's text
 * and replaces them with a styled span element that displays the formatted price.
 * The styling is determined by the size attribute of the shortcode.
 *
 * @param props - Function parameters
 * @param props.context - Shortcode context containing the text to be updated
 * @param props.price - The price value to display in place of the shortcode
 *
 * @returns void - Updates the context.text directly with the replaced shortcodes
 *
 * Styling:
 * - Wraps the price in a styled span element
 * - Applies typography styles based on the size attribute
 *
 * @example
 * ```ts
 * const context = { text: "The total is [price size='large']$10[/price].", locale: "en" };
 * applyPrice({ context, price: 10 });
 * // context.text will be updated to: "The total is <span class='...'>10</span>."
 * ```
 */
import { classNames } from '@payfit/growth-platform-brand-system-v2';
import { ShortcodeContext } from '../..';
import { applyShortcode } from '../../utils/applyShortcode';
import { fontHeading2 } from '@payfit/growth-platform-brand-system-v2/styles/typographyNew';
import { formatPrice } from '../../../formatPrice';
export const applyPrice = ({
  context,
  price,
}: {
  context: ShortcodeContext;
  price: number;
}) => {
  context.text = applyShortcode(context.text, 'price', (match) => {
    const { attributes } = match;
    const { size } = attributes;

    return `<span class="${classNames(size === 'large' && fontHeading2, 'text-[#042040]')}">${formatPrice(price, context.locale)}</span>`;
  });
};
